import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import PlatformPageHeader from "./PlatformPageHeader";
import {
  CityQuickPick,
  LeadBoard,
  LeadFunnel,
  LeadSummaryCards,
  mergeCityOptions,
  PRESET_CITIES,
} from "./PlatformLeadsParts";
import s from "./platformAdmin.module.css";
import { downloadExcelCsv, parseExcelCsv } from "../../../utils/excelCsv";

const STATUS_FILTERS = [
  { id: "", label: "Все" },
  { id: "new", label: "Новые" },
  { id: "contacted", label: "Связались" },
  { id: "replied", label: "Ответили" },
  { id: "demo", label: "Демо" },
  { id: "trial", label: "Триал" },
  { id: "paid", label: "Оплатили" },
  { id: "lost", label: "Отказ" },
  { id: "do_not_contact", label: "Не беспокоить" },
];

const SCRAPE_SOURCES = [
  {
    id: "osm",
    label: "OpenStreetMap",
    hint: "Бесплатно · секции и клубы на карте",
    env: "ключ не нужен",
    free: true,
  },
  {
    id: "vk",
    label: "VK",
    hint: "Бесплатно · сообщества и группы",
    env: "VK_API_TOKEN",
    free: true,
  },
  {
    id: "2gis",
    label: "2GIS",
    hint: "Платно · телефоны и адреса",
    env: "DGIS_API_KEY",
    optional: true,
  },
  {
    id: "yandex",
    label: "Яндекс.Карты",
    hint: "Платный API · не рекомендуется",
    env: "YANDEX_MAPS_API_KEY",
    optional: true,
  },
  {
    id: "avito",
    label: "Avito",
    hint: "Нестабильно · блокирует сервер (429)",
    env: "AVITO_SCRAPE_ENABLED=true",
    optional: true,
  },
];

const DEFAULT_QUERIES = [
  "секция карате",
  "школа танцев",
  "детская секция футбол",
  "секция единоборств",
  "спортивный клуб детский",
];

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function parseCsv(text) {
  const { headers, rows } = parseExcelCsv(text);
  if (!headers.length || !rows.length) return [];
  const alias = {
    название: "name",
    name: "name",
    город: "city",
    city: "city",
    телефон: "phone",
    phone: "phone",
    email: "email",
    почта: "email",
    сайт: "website",
    website: "website",
    vk: "vk_url",
    vk_url: "vk_url",
    адрес: "address",
    address: "address",
    спорт: "sport_type",
    sport_type: "sport_type",
    вид_спорта: "sport_type",
    заметки: "notes",
    notes: "notes",
    статус: "status",
    status: "status",
    источник: "source",
    source: "source",
  };

  return rows.map((cols) => {
    const row = {};
    headers.forEach((header, index) => {
      const key = alias[header] || header;
      row[key] = cols[index] || "";
    });
    return row;
  });
}

const LEADS_CSV_COLUMNS = [
  { key: "name", header: "Название" },
  { key: "city", header: "Город" },
  { key: "sport_type", header: "Вид спорта" },
  { key: "phone", header: "Телефон" },
  { key: "email", header: "Email" },
  { key: "website", header: "Сайт" },
  { key: "address", header: "Адрес" },
  { key: "status", header: "Статус" },
  { key: "source", header: "Источник" },
  { key: "notes", header: "Заметки" },
];

function downloadLeadsCsv(leads) {
  downloadExcelCsv(
    `baysport-leads-${new Date().toISOString().slice(0, 10)}.csv`,
    LEADS_CSV_COLUMNS.map((c) => c.header),
    (leads || []).map((lead) =>
      LEADS_CSV_COLUMNS.map((c) => String(lead[c.key] ?? "").replace(/\n/g, " ")),
    ),
  );
}

export default function PlatformLeads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [scrapeCity, setScrapeCity] = useState(PRESET_CITIES[0]);
  const [scraping, setScraping] = useState(false);
  const [importing, setImporting] = useState(false);
  const [scrapeSources, setScrapeSources] = useState(["osm"]);
  const [toolsOpen, setToolsOpen] = useState(true);
  const [viewMode, setViewMode] = useState("board");

  const cityOptions = useMemo(() => mergeCityOptions(stats), [stats]);

  const toggleSource = (id) => {
    setScrapeSources((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [listData, statsData] = await Promise.all([
        platformAPI.listLeads({
          status: viewMode === "board" ? undefined : statusFilter || undefined,
          city: cityFilter || undefined,
          q: query || undefined,
          limit: 200,
        }),
        platformAPI.getLeadStats(),
      ]);
      setLeads(listData.leads || []);
      setTotal(listData.total || 0);
      setStats(statsData);
    } catch (e) {
      setErr(e.response?.data?.error || "Не удалось загрузить лиды");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, cityFilter, query, viewMode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!msg) return undefined;
    const timer = window.setTimeout(() => setMsg(""), 5000);
    return () => window.clearTimeout(timer);
  }, [msg]);

  const statusOptions = useMemo(() => {
    if (stats?.status_labels) {
      return Object.entries(stats.status_labels).map(([id, label]) => ({
        id,
        label,
      }));
    }
    return STATUS_FILTERS.filter((f) => f.id);
  }, [stats]);

  const filteredLeads = useMemo(() => {
    if (viewMode !== "board" || !statusFilter) return leads;
    return leads.filter((lead) => lead.status === statusFilter);
  }, [leads, statusFilter, viewMode]);

  const updateLead = async (id, patch) => {
    try {
      const data = await platformAPI.updateLead(id, patch);
      setLeads((prev) => prev.map((row) => (row.id === id ? data.lead : row)));
      setMsg("Сохранено");
      platformAPI.getLeadStats().then(setStats).catch(() => {});
    } catch (e) {
      setErr(e.response?.data?.error || "Ошибка сохранения");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Удалить лид из базы?")) return;
    try {
      await platformAPI.deleteLead(id);
      setLeads((prev) => prev.filter((row) => row.id !== id));
      setMsg("Лид удалён");
      platformAPI.getLeadStats().then(setStats).catch(() => {});
    } catch (e) {
      setErr(e.response?.data?.error || "Не удалось удалить");
    }
  };

  const runScrape = async () => {
    if (!scrapeCity.trim()) {
      setErr("Укажите город для парсинга");
      return;
    }
    if (!scrapeSources.length) {
      setErr("Выберите хотя бы один источник");
      return;
    }
    setScraping(true);
    setErr("");
    setMsg("");
    try {
      const data = await platformAPI.scrapeLeads({
        city: scrapeCity.trim(),
        sources: scrapeSources,
        queries: DEFAULT_QUERIES,
        limit_per_query: 20,
      });
      const bySource = (data.by_source || [])
        .map((row) => `${row.source}: +${row.created}`)
        .join(", ");
      const errorHints = (data.errors || [])
        .map((row) => row.message)
        .filter(Boolean)
        .slice(0, 2)
        .join(" · ");

      if ((data.created || 0) + (data.updated || 0) === 0) {
        if (data.errors?.length) {
          setErr(
            errorHints ||
              "Лиды не добавлены. Проверьте источники, ключи API или выберите другой город.",
          );
        } else {
          setMsg(
            `Парсинг завершён, но новых лидов для «${scrapeCity.trim()}» не найдено. Попробуйте Москву/СПб, VK или импорт CSV.`,
          );
        }
      } else {
        setMsg(
          `Готово: +${data.created}, обновлено ${data.updated}, пропущено ${data.skipped}${
            bySource ? ` · ${bySource}` : ""
          }${errorHints ? ` · ${errorHints}` : ""}`,
        );
      }
      await load();
    } catch (e) {
      if (e.code === "ECONNABORTED" || /timeout/i.test(e.message || "")) {
        setErr(
          "Парсинг занял слишком много времени. Выберите один источник (OpenStreetMap) и повторите.",
        );
      } else {
        setErr(e.response?.data?.error || e.message || "Парсинг не выполнен");
      }
    } finally {
      setScraping(false);
    }
  };

  const onImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImporting(true);
    setErr("");
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        setErr("В файле нет строк данных");
        return;
      }
      const data = await platformAPI.importLeads({ rows, source: "csv" });
      setMsg(
        `Импорт: +${data.created}, обновлено ${data.updated}, пропущено ${data.skipped}`,
      );
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || "Импорт не выполнен");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={s.page}>
      <PlatformPageHeader
        title="Лиды"
        lead="Холодные продажи: парсинг секций, воронка касаний, переход в триал и оплату."
        actions={
          <>
            <Link to="/admin/onboarding-emails" className={s.heroActionBtnGhost}>
              Письма онбординга
            </Link>
            <button
              type="button"
              className={s.heroActionBtnGhost}
              onClick={() => setToolsOpen((v) => !v)}
            >
              {toolsOpen ? "Скрыть парсинг" : "Парсинг / импорт"}
            </button>
          </>
        }
      />

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      {toolsOpen ? (
        <section className={s.exportPanel} aria-label="Парсинг и импорт лидов">
          <div className={s.exportPanelHead}>
            <div>
              <h2 className={s.exportPanelTitle}>Наполнение базы</h2>
              <p className={s.exportPanelLead}>
                Бесплатно: <strong>OpenStreetMap</strong> (без ключа) и{" "}
                <strong>VK</strong> (сервисный ключ). Платные: 2GIS, Яндекс. CSV —
                всегда бесплатно.
              </p>
            </div>
          </div>

          <CityQuickPick
            label="Город для парсинга"
            value={scrapeCity}
            onChange={setScrapeCity}
            cities={cityOptions}
            datalistId="lead-scrape-cities"
          />

          <div className={s.exportSegmentGrid}>
            {SCRAPE_SOURCES.map((src) => {
              const active = scrapeSources.includes(src.id);
              return (
                <button
                  key={src.id}
                  type="button"
                  className={`${s.exportSegmentCard} ${active ? s.exportSegmentCardActive : ""}`}
                  onClick={() => toggleSource(src.id)}
                >
                  <strong>{src.label}</strong>
                  <span>{src.hint}</span>
                  <em>
                    {active ? "Выбран" : "Добавить"} · {src.env}
                    {src.free ? " · бесплатно" : src.optional ? " · опционально" : ""}
                  </em>
                </button>
              );
            })}
          </div>

          <div className={s.clubsToolbarRow}>
            <button
              type="button"
              className={s.heroActionBtn}
              disabled={scraping}
              onClick={runScrape}
            >
              {scraping ? "Парсим…" : "Запустить парсинг"}
            </button>
            <label className={s.heroActionBtnGhost}>
              {importing ? "Импорт…" : "Импорт CSV"}
              <input type="file" accept=".csv,.txt" onChange={onImportFile} hidden />
            </label>
            <button
              type="button"
              className={s.heroActionBtnGhost}
              onClick={() => downloadLeadsCsv(leads)}
              disabled={!leads.length}
            >
              Экспорт CSV
            </button>
          </div>

          <details className={s.leadHelpDetails}>
            <summary>Как связаны лиды и клубы</summary>
            <p>
              Лид и клуб — разные сущности. Если владелец регистрирует клуб с тем же
              email или телефоном, лид автоматически переходит в «Триал» (или удаляется,
              если PLATFORM_LEAD_ON_REGISTER=delete). После оплаты — «Оплатил».
            </p>
            <p>CSV: Название;Город;Телефон;Email;Адрес;Вид спорта;Заметки</p>
          </details>
        </section>
      ) : null}

      <LeadSummaryCards
        stats={stats}
        activeStatus={statusFilter}
        onSelectStatus={setStatusFilter}
      />

      <LeadFunnel
        stats={stats}
        activeStatus={statusFilter}
        onSelectStatus={setStatusFilter}
      />

      <div className={s.clubsPageBody}>
        <div className={s.leadListToolbar}>
          <div className={s.leadListToolbarTop}>
            <input
              type="search"
              className={s.clubSearch}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск: название, телефон, email…"
            />
            <div className={s.leadViewToggle}>
              <button
                type="button"
                className={`${s.leadViewToggleBtn} ${
                  viewMode === "board" ? s.leadViewToggleBtnActive : ""
                }`}
                onClick={() => setViewMode("board")}
              >
                Доска
              </button>
              <button
                type="button"
                className={`${s.leadViewToggleBtn} ${
                  viewMode === "table" ? s.leadViewToggleBtnActive : ""
                }`}
                onClick={() => setViewMode("table")}
              >
                Таблица
              </button>
            </div>
          </div>

          <CityQuickPick
            label="Фильтр по городу"
            value={cityFilter}
            onChange={setCityFilter}
            cities={cityOptions}
            allowAll
            datalistId="lead-filter-cities"
          />

          {viewMode === "table" ? (
            <div className={s.clubsFilterChips} role="tablist" aria-label="Статус лида">
              {STATUS_FILTERS.map((item) => (
                <button
                  key={item.id || "all"}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === item.id}
                  className={`${s.clubsFilterChip} ${
                    statusFilter === item.id ? s.clubsFilterChipActive : ""
                  }`}
                  onClick={() => setStatusFilter(item.id)}
                >
                  {item.label}
                  {stats?.by_status?.[item.id] != null && item.id
                    ? ` · ${stats.by_status[item.id]}`
                    : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className={s.loading}>Загрузка лидов…</div>
        ) : !leads.length ? (
          <div className={s.leadEmptyState}>
            <strong>Лидов пока нет</strong>
            <p>Выберите город и запустите парсинг — или импортируйте CSV с контактами.</p>
            <button type="button" className={s.heroActionBtn} onClick={() => setToolsOpen(true)}>
              Открыть парсинг
            </button>
          </div>
        ) : viewMode === "board" ? (
          <>
            <p className={s.exportPanelLead}>
              {statusFilter
                ? `Фильтр: ${stats?.status_labels?.[statusFilter] || statusFilter} · `
                : ""}
              Показано {filteredLeads.length} из {total}
            </p>
            <LeadBoard
              leads={filteredLeads}
              statusOptions={statusOptions}
              onUpdate={updateLead}
              onDelete={deleteLead}
            />
          </>
        ) : (
          <>
            <p className={s.exportPanelLead}>
              Показано {leads.length} из {total}
            </p>
            <div className={s.tableWrap}>
              <table className={s.dataTable}>
                <thead>
                  <tr>
                    <th>Клуб / секция</th>
                    <th>Контакты</th>
                    <th>Город</th>
                    <th>Источник</th>
                    <th>Статус</th>
                    <th>Заметки</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <td>
                        <strong>{lead.name}</strong>
                        {lead.sport_type ? (
                          <div className={s.exportPanelLead}>{lead.sport_type}</div>
                        ) : null}
                        {lead.address ? (
                          <div className={s.exportPanelLead}>{lead.address}</div>
                        ) : null}
                      </td>
                      <td>
                        {lead.phone ? <div>{lead.phone}</div> : null}
                        {lead.email ? (
                          <div>
                            <a href={`mailto:${lead.email}`}>{lead.email}</a>
                          </div>
                        ) : null}
                        {lead.website ? (
                          <div>
                            <a href={lead.website} target="_blank" rel="noreferrer">
                              сайт
                            </a>
                          </div>
                        ) : null}
                        {!lead.phone && !lead.email && !lead.website ? "—" : null}
                      </td>
                      <td>{lead.city || "—"}</td>
                      <td>
                        {lead.source}
                        {lead.source_url ? (
                          <div>
                            <a href={lead.source_url} target="_blank" rel="noreferrer">
                              карточка
                            </a>
                          </div>
                        ) : null}
                        <div className={s.exportPanelLead}>{formatWhen(lead.createdAt)}</div>
                      </td>
                      <td>
                        <select
                          className={s.leadTableSelect}
                          value={lead.status}
                          onChange={(e) => updateLead(lead.id, { status: e.target.value })}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <textarea
                          className={s.leadTableNotes}
                          defaultValue={lead.notes || ""}
                          rows={2}
                          placeholder="Комментарий…"
                          onBlur={(e) => {
                            const next = e.target.value.trim();
                            if (next !== (lead.notes || "")) {
                              updateLead(lead.id, { notes: next });
                            }
                          }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className={s.heroActionBtnDanger}
                          onClick={() => deleteLead(lead.id)}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
